import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import {
  Container,
  Typography,
  IconButton,
  Tooltip,
  Fab,
  keyframes,
  Button,
} from "@mui/material";
import { Settings } from "@mui/icons-material";
import MenuIcon from "@mui/icons-material/Menu";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { useTheme, useMediaQuery } from "@mui/material";
import useScrollTrigger from "@mui/material/useScrollTrigger";
import MobileDrawer from "./MobileDrawer";
import NavigationLinks from "./NavigationLinks";
import CutomizeDrawer from "./CutomizeDrawer";
import { name } from "../config";
import { pages } from "../pages";

interface AppbarProps {
  hideNavLinks?: boolean;
  hideLanguageMenu?: boolean;
  onTogglePortfolio?: () => void;
}

const rotateAnimation = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const Appbar: React.FC<AppbarProps> = ({ 
  hideNavLinks = false, 
  hideLanguageMenu = false,
  onTogglePortfolio
}) => {
  const { t } = useTranslation();
  const trigger = useScrollTrigger();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const toggleDrawer = () => {
    setDrawerOpen(!isDrawerOpen);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <AppBar
      color="inherit"
      sx={{ boxShadow: trigger ? 4 : "none", transition: "box-shadow 0.3s" }}
    >
      <Container>
        <Toolbar
          disableGutters
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* Only show menu icon if navigation links aren't hidden and on mobile */}
          {isMobile && !hideNavLinks && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, order: 0 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Typography
            variant="h5"
            sx={{
              fontStyle: "italic",
              textDecoration: "underline",
              flexGrow: 0,
              textAlign: isMobile ? "center" : "left",
            }}
          >
            {t(name)}
          </Typography>

          {/* Only show navigation links if they're not hidden and on desktop */}
          <Box
            sx={{
              flexGrow: 1,
              display: { xs: "none", md: "flex" },
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            {!hideNavLinks && <NavigationLinks pages={pages} />}
            
            {/* Go To Agent button - always show in portfolio mode */}
            {!hideNavLinks && onTogglePortfolio && (
              <Button
                color="primary"
                onClick={onTogglePortfolio}
                startIcon={<SmartToyIcon />}
                sx={{ 
                  ml: 2,
                  fontWeight: "medium",
                  textTransform: "none",
                  borderRadius: 2,
                  bgcolor: theme.palette.mode === 'dark' ? '#10a37f' : '#19c37d',
                  color: '#fff',
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark' ? '#0e8c6a' : '#0ea67a',
                  },
                }}
                variant="contained"
              >
                {t("Talk To Agent")}
              </Button>
            )}
            
            {/* Checkout Portfolio button - only show when in chat mode */}
            {hideNavLinks && onTogglePortfolio && (
              <Button
                color="primary"
                onClick={onTogglePortfolio}
                sx={{ 
                  ml: 2,
                  fontWeight: "bold",
                  textTransform: "none",
                }}
                variant="outlined"
              >
                {t("Check Portfolio")}
              </Button>
            )}
          </Box>

          {/* Language selector is now conditional based on hideLanguageMenu prop */}
          {!isMobile && !hideLanguageMenu && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {/* Removed LanguageSelector component */}
            </Box>
          )}

          {/* For mobile, add Portfolio button if in chat mode */}
          {isMobile && hideNavLinks && onTogglePortfolio && (
            <Button
              color="primary"
              onClick={onTogglePortfolio}
              sx={{ 
                ml: 1,
                fontWeight: "bold",
                textTransform: "none",
              }}
              variant="outlined"
              size="small"
            >
              {t("Portfolio")}
            </Button>
          )}
          
          {/* Add Go To Agent button for mobile in portfolio mode */}
          {isMobile && !hideNavLinks && onTogglePortfolio && (
            <Button
              color="primary"
              onClick={onTogglePortfolio}
              sx={{ 
                ml: 1,
                fontWeight: "medium",
                textTransform: "none",
                bgcolor: theme.palette.mode === 'dark' ? '#10a37f' : '#19c37d',
                color: '#fff',
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark' ? '#0e8c6a' : '#0ea67a',
                },
              }}
              variant="contained"
              size="small"
              startIcon={<SmartToyIcon fontSize="small" />}
            >
              {t("Agent")}
            </Button>
          )}
        </Toolbar>
      </Container>

      {/* Only show customization button if not in chat mode */}
      {!hideNavLinks && (
        <Tooltip arrow title="Live Customize">
          <Fab
            component="div"
            onClick={toggleDrawer}
            size="small"
            variant="circular"
            color="primary"
            sx={{
              borderRadius: 0,
              borderTopLeftRadius: "50%",
              borderBottomLeftRadius: "50%",
              borderTopRightRadius: "50%",
              borderBottomRightRadius: "4px",
              top: "40%",
              position: "fixed",
              right: 10,
              zIndex: theme.zIndex.speedDial,
            }}
          >
            <IconButton
              color="inherit"
              size="large"
              disableRipple
              onClick={toggleDrawer}
              sx={{
                animation: `${rotateAnimation} 2s linear infinite`,
              }}
            >
              <Settings />
            </IconButton>
          </Fab>
        </Tooltip>
      )}

      {/* Only include mobile drawer if not in chat mode */}
      {!hideNavLinks && (
        <MobileDrawer
          open={mobileOpen}
          onClose={handleDrawerToggle}
          pages={pages}
        />
      )}

      {/* Only include customize drawer if not in chat mode */}
      {!hideNavLinks && (
        <CutomizeDrawer open={isDrawerOpen} setDrawerOpen={setDrawerOpen} />
      )}
    </AppBar>
  );
};

export default Appbar;
